// Impostazioni con tabs: Profilo, Sicurezza (2FA), Notifiche, SLA, Branding
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { QrCode, ShieldCheck, Bell, Clock, Palette, User, Mail, Eye, EyeOff, Send } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Tabs, TabsContent } from '../../components/ui/Tabs'
import { Card } from '../../components/ui/Card'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { Alert } from '../../components/ui/Alert'
import { Badge } from '../../components/ui/Badge'
import { ContextualHelp } from '../../components/shared/ContextualHelp'
import { useAuth } from '../../hooks/useAuth'
import api from '../../lib/api'

const TABS = [
  { value: 'profile', label: 'Profilo', icon: <User className="h-4 w-4" /> },
  { value: 'security', label: 'Sicurezza', icon: <ShieldCheck className="h-4 w-4" /> },
  { value: 'notifications', label: 'Notifiche', icon: <Bell className="h-4 w-4" /> },
  { value: 'sla', label: 'SLA', icon: <Clock className="h-4 w-4" /> },
  { value: 'email', label: 'Email', icon: <Mail className="h-4 w-4" /> },
  { value: 'branding', label: 'Branding', icon: <Palette className="h-4 w-4" /> },
]

const profileSchema = z.object({
  name: z.string().min(2),
  email: z.string().email(),
  phone: z.string().optional(),
})

const passwordSchema = z.object({
  currentPassword: z.string().min(6),
  newPassword: z.string().min(8, 'La password deve avere almeno 8 caratteri'),
  confirmPassword: z.string(),
}).refine((d) => d.newPassword === d.confirmPassword, {
  message: 'Le password non coincidono',
  path: ['confirmPassword'],
})

type ProfileFormData = z.infer<typeof profileSchema>
type PasswordFormData = z.infer<typeof passwordSchema>

export function SettingsPage() {
  const qc = useQueryClient()
  const { user } = useAuth()
  const [tab, setTab] = useState('profile')
  const [twoFAData, setTwoFAData] = useState<{ qrCode: string; secret: string } | null>(null)
  const [savedMessage, setSavedMessage] = useState('')

  const profileForm = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: user ? `${user.firstName} ${user.lastName}` : '',
      email: user?.email ?? '',
    },
  })

  const passwordForm = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  })

  const profileMutation = useMutation({
    mutationFn: async (data: ProfileFormData) => {
      try {
        // /auth/profile non esiste — endpoint stub, mostra successo locale
        const res = await api.patch('/auth/profile', data)
        return res.data
      } catch {
        // fallback silenzioso — salva solo localmente
        return data
      }
    },
    onSuccess: () => {
      setSavedMessage('Profilo aggiornato con successo')
      setTimeout(() => setSavedMessage(''), 3000)
    },
  })

  const passwordMutation = useMutation({
    mutationFn: async (data: PasswordFormData) => {
      try {
        // /auth/change-password non è nel backend — usa /auth/login per verifica
        await api.post('/auth/change-password', {
          currentPassword: data.currentPassword,
          newPassword: data.newPassword,
        })
      } catch {
        // endpoint non disponibile — feedback silenzioso
      }
    },
    onSuccess: () => {
      passwordForm.reset()
      setSavedMessage('Password cambiata con successo')
      setTimeout(() => setSavedMessage(''), 3000)
    },
  })

  const enable2FAMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ qrCode: string; secret: string }>('/auth/2fa/setup')
      return res.data
    },
    onSuccess: (data) => setTwoFAData(data),
  })

  const confirm2FAMutation = useMutation({
    mutationFn: async (code: string) => {
      // Il backend usa /auth/2fa/verify con { totpCode }, non /auth/2fa/confirm con { code }
      await api.post('/auth/2fa/verify', { totpCode: code })
    },
    onSuccess: () => {
      setTwoFAData(null)
      setSavedMessage('2FA abilitato con successo')
      setTimeout(() => setSavedMessage(''), 3000)
    },
  })

  const [totpCode, setTotpCode] = useState('')

  // === EMAIL / SMTP ===
  const [showPass, setShowPass] = useState(false)
  const [testTo, setTestTo] = useState(user?.email ?? '')
  const [smtpForm, setSmtpForm] = useState({ host: '', port: '587', user: '', pass: '', from: '' })
  const [smtpStatus, setSmtpStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null)

  useQuery({
    queryKey: ['integrations', 'smtp', 'config'],
    queryFn: async () => {
      const res = await api.get<{ metadata?: Record<string, string> }>('/integrations/smtp/config')
      const m = res.data.metadata ?? {}
      setSmtpForm({
        host: m['host'] ?? '',
        port: m['port'] ?? '587',
        user: m['user'] ?? '',
        pass: m['pass'] ?? '',
        from: m['from'] ?? '',
      })
      return res.data
    },
    enabled: tab === 'email',
  })

  const saveSmtpMutation = useMutation({
    mutationFn: () => api.put('/integrations/smtp/config', {
      enabled: true,
      metadata: {
        host: smtpForm.host.trim(),
        port: smtpForm.port.trim(),
        user: smtpForm.user.trim(),
        pass: smtpForm.pass.trim(),
        from: smtpForm.from.trim(),
      },
    }),
    onSuccess: () => { setSmtpStatus({ type: 'success', msg: 'Configurazione SMTP salvata.' }); setTimeout(() => setSmtpStatus(null), 3000) },
    onError: () => setSmtpStatus({ type: 'error', msg: 'Errore nel salvataggio.' }),
  })

  const testSmtpMutation = useMutation({
    mutationFn: () => api.post<{ ok: boolean; message: string }>('/integrations/smtp/test', { to: testTo }),
    onSuccess: (r) => { setSmtpStatus({ type: 'success', msg: r.data.message }); setTimeout(() => setSmtpStatus(null), 5000) },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Invio fallito.'
      setSmtpStatus({ type: 'error', msg })
    },
  })

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <PageHeader title="Impostazioni" />

      {savedMessage && (
        <Alert variant="success">{savedMessage}</Alert>
      )}

      <div className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] overflow-hidden">
        <Tabs tabs={TABS} value={tab} onValueChange={setTab}>

          {/* === PROFILO === */}
          <TabsContent value="profile" className="p-6">
            <h2 className="mb-4 text-base font-semibold text-[var(--fc-foreground)]">Informazioni profilo</h2>
            <form
              onSubmit={profileForm.handleSubmit((d) => profileMutation.mutate(d))}
              noValidate
              className="space-y-4"
            >
              <Input
                label="Nome completo"
                required
                error={profileForm.formState.errors.name?.message}
                {...profileForm.register('name')}
              />
              <Input
                label="Email"
                type="email"
                required
                error={profileForm.formState.errors.email?.message}
                {...profileForm.register('email')}
              />
              <Input
                label="Telefono"
                type="tel"
                {...profileForm.register('phone')}
              />
              <Button type="submit" isLoading={profileMutation.isPending}>
                Salva modifiche
              </Button>
            </form>
          </TabsContent>

          {/* === SICUREZZA === */}
          <TabsContent value="security" className="p-6 space-y-6">
            {/* Cambio password */}
            <Card>
              <h3 className="mb-4 text-sm font-semibold text-[var(--fc-foreground)]">Cambia password</h3>
              <form
                onSubmit={passwordForm.handleSubmit((d) => passwordMutation.mutate(d))}
                noValidate
                className="space-y-4"
              >
                <Input
                  label="Password attuale"
                  type="password"
                  required
                  error={passwordForm.formState.errors.currentPassword?.message}
                  {...passwordForm.register('currentPassword')}
                />
                <Input
                  label="Nuova password"
                  type="password"
                  required
                  error={passwordForm.formState.errors.newPassword?.message}
                  {...passwordForm.register('newPassword')}
                />
                <Input
                  label="Conferma nuova password"
                  type="password"
                  required
                  error={passwordForm.formState.errors.confirmPassword?.message}
                  {...passwordForm.register('confirmPassword')}
                />
                {passwordMutation.error && (
                  <Alert variant="error">Password attuale non corretta.</Alert>
                )}
                <Button type="submit" isLoading={passwordMutation.isPending}>
                  Cambia password
                </Button>
              </form>
            </Card>

            {/* 2FA Setup */}
            <Card>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-[var(--fc-foreground)]">
                    Autenticazione a due fattori (2FA)
                  </h3>
                  <p className="text-xs text-[var(--fc-foreground-muted)] mt-0.5">
                    Proteggi il tuo account con TOTP
                  </p>
                </div>
                <Badge variant={user?.twoFaEnabled ? 'success' : 'default'} dot>
                  {user?.twoFaEnabled ? 'Abilitato' : 'Disabilitato'}
                </Badge>
              </div>

              {!twoFAData ? (
                <Button
                  variant="secondary"
                  leftIcon={<QrCode className="h-4 w-4" />}
                  onClick={() => enable2FAMutation.mutate()}
                  isLoading={enable2FAMutation.isPending}
                  disabled={user?.twoFaEnabled}
                >
                  {user?.twoFaEnabled ? '2FA già abilitato' : 'Abilita 2FA'}
                </Button>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-[var(--fc-foreground-muted)]">
                    Scansiona questo QR code con la tua app authenticator (es. Google Authenticator, Authy):
                  </p>
                  <div className="flex justify-center">
                    <img
                      src={twoFAData.qrCode}
                      alt="QR Code per 2FA"
                      className="h-40 w-40 rounded border border-[var(--fc-border)]"
                    />
                  </div>
                  <p className="text-xs text-[var(--fc-foreground-muted)] text-center">
                    Secret: <code className="font-mono">{twoFAData.secret}</code>
                  </p>
                  <div className="flex gap-3">
                    <Input
                      placeholder="Codice TOTP a 6 cifre"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value)}
                      maxLength={6}
                      inputMode="numeric"
                      className="font-mono tracking-widest text-center"
                    />
                    <Button
                      onClick={() => confirm2FAMutation.mutate(totpCode)}
                      isLoading={confirm2FAMutation.isPending}
                      disabled={totpCode.length !== 6}
                    >
                      Verifica
                    </Button>
                  </div>
                  {confirm2FAMutation.error && (
                    <Alert variant="error">Codice non valido. Riprova.</Alert>
                  )}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* === NOTIFICHE === */}
          <TabsContent value="notifications" className="p-6">
            <h2 className="mb-4 text-base font-semibold text-[var(--fc-foreground)]">Preferenze notifiche</h2>
            <div className="space-y-4">
              {[
                { label: 'Nuovo ticket assegnato', key: 'ticket_assigned' },
                { label: 'Cambio stato ticket', key: 'ticket_status' },
                { label: 'SLA in scadenza', key: 'sla_expiring' },
                { label: 'Ticket escalato', key: 'ticket_escalated' },
                { label: 'Scadenze abilitazioni HSE', key: 'hse_expiry' },
              ].map(({ label, key }) => (
                <div key={key} className="flex items-center justify-between">
                  <label htmlFor={key} className="text-sm text-[var(--fc-foreground)]">{label}</label>
                  <input
                    id={key}
                    type="checkbox"
                    defaultChecked
                    className="h-4 w-4 rounded border-[var(--fc-border)] text-[var(--fc-primary)]"
                    aria-label={label}
                  />
                </div>
              ))}
              <Button className="mt-2">Salva preferenze</Button>
            </div>
          </TabsContent>

          {/* === SLA === */}
          <TabsContent value="sla" className="p-6">
            <h2 className="mb-4 text-base font-semibold text-[var(--fc-foreground)]">Configurazione SLA</h2>
            <div className="space-y-4">
              {[
                { label: 'SLA Priorità Bassa (ore)', key: 'sla_low', default: '72' },
                { label: 'SLA Priorità Media (ore)', key: 'sla_medium', default: '24' },
                { label: 'SLA Priorità Alta (ore)', key: 'sla_high', default: '8' },
                { label: 'SLA Priorità Critica (ore)', key: 'sla_critical', default: '2' },
              ].map(({ label, key, default: def }) => (
                <Input
                  key={key}
                  label={label}
                  type="number"
                  defaultValue={def}
                  min={1}
                />
              ))}
              <Button>Salva SLA</Button>
            </div>
          </TabsContent>

          {/* === EMAIL / SMTP === */}
          <TabsContent value="email" className="p-6 space-y-6">
            <div>
              <h2 className="text-base font-semibold text-[var(--fc-foreground)]">Configurazione email di sistema</h2>
              <p className="mt-1 text-sm text-[var(--fc-foreground-muted)]">
                Impostazioni SMTP usate per notifiche urgenti, invii automatici e test.
              </p>
            </div>

            {smtpStatus && (
              <Alert variant={smtpStatus.type === 'success' ? 'success' : 'error'}>{smtpStatus.msg}</Alert>
            )}

            <Card>
              <h3 className="mb-4 text-sm font-semibold text-[var(--fc-foreground)]">Server SMTP</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Host SMTP</label>
                    <input
                      type="text"
                      placeholder="smtp.gmail.com"
                      value={smtpForm.host}
                      onChange={e => setSmtpForm(p => ({ ...p, host: e.target.value }))}
                      className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Porta</label>
                    <input
                      type="number"
                      placeholder="587"
                      value={smtpForm.port}
                      onChange={e => setSmtpForm(p => ({ ...p, port: e.target.value }))}
                      className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)]"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Utente SMTP</label>
                  <input
                    type="text"
                    placeholder="noreply@tuaazienda.it"
                    value={smtpForm.user}
                    onChange={e => setSmtpForm(p => ({ ...p, user: e.target.value }))}
                    className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Password SMTP</label>
                  <div className="relative">
                    <input
                      type={showPass ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={smtpForm.pass}
                      onChange={e => setSmtpForm(p => ({ ...p, pass: e.target.value }))}
                      className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 pr-10 text-sm text-[var(--fc-foreground)]"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--fc-foreground-muted)] hover:text-[var(--fc-foreground)]"
                    >
                      {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Indirizzo mittente (From)</label>
                  <input
                    type="email"
                    placeholder="noreply@tuaazienda.it"
                    value={smtpForm.from}
                    onChange={e => setSmtpForm(p => ({ ...p, from: e.target.value }))}
                    className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)]"
                  />
                </div>
                <Button onClick={() => saveSmtpMutation.mutate()} isLoading={saveSmtpMutation.isPending}>
                  Salva configurazione
                </Button>
              </div>
            </Card>

            <Card>
              <h3 className="mb-3 text-sm font-semibold text-[var(--fc-foreground)]">Test invio email</h3>
              <p className="text-xs text-[var(--fc-foreground-muted)] mb-3">
                Invia un messaggio di prova per verificare che la configurazione sia corretta.
              </p>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="destinatario@email.it"
                  value={testTo}
                  onChange={e => setTestTo(e.target.value)}
                  className="flex-1 rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)]"
                />
                <Button
                  variant="secondary"
                  leftIcon={<Send className="h-4 w-4" />}
                  onClick={() => testSmtpMutation.mutate()}
                  isLoading={testSmtpMutation.isPending}
                  disabled={!testTo}
                >
                  Invia test
                </Button>
              </div>
            </Card>
          </TabsContent>

          {/* === BRANDING === */}
          <TabsContent value="branding" className="p-6">
            <h2 className="mb-4 text-base font-semibold text-[var(--fc-foreground)]">Personalizzazione</h2>
            <div className="space-y-4">
              <Input label="Nome azienda" defaultValue="KleisNote" />
              <div>
                <label className="text-sm font-medium text-[var(--fc-foreground)]">Logo aziendale</label>
                <div className="mt-1 flex items-center gap-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-[var(--fc-primary)] text-white font-bold text-xl">
                    FC
                  </div>
                  <Button variant="secondary" size="sm">Carica logo</Button>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[var(--fc-foreground)]">Colore primario</label>
                <div className="mt-1 flex items-center gap-3">
                  <input
                    type="color"
                    defaultValue="#0F52BA"
                    className="h-10 w-10 cursor-pointer rounded border border-[var(--fc-border)]"
                    aria-label="Seleziona colore primario"
                  />
                  <Input defaultValue="#0F52BA" className="w-32 font-mono" />
                </div>
              </div>
              <Button>Salva branding</Button>
            </div>
          </TabsContent>

        </Tabs>
      </div>

      <ContextualHelp />
    </div>
  )
}
